import { useAtomValue } from 'jotai';

import { Button, SkeletonRect, Stack } from '@ekhodealer/ekho-common/rev';

import { AccordionSection } from '@common/components/AccordionSection';
import { InfoLineItem } from '@common/components/InfoLineItem';

import { useGetOrderDetails } from '../../api/useGetOrderDetails';
import { orderDetailsDrawerAtom } from '../../atoms/orderDetailsDrawerAtoms';

import { SectionSkeleton } from './shared/SectionSkeleton';

const SECTION_TITLE = 'Buyer details';

interface BuyerDetailsContentProps {
	purchasedVehicleId: string;
	showAllFields?: boolean;
}

export const BuyerDetailsContent = ({
	purchasedVehicleId,
	showAllFields = true,
}: BuyerDetailsContentProps) => {
	const { data: orderDetails, isLoading } = useGetOrderDetails(purchasedVehicleId);

	if (isLoading) {
		return <SkeletonRect width='100%' height={120} />;
	}

	if (!orderDetails) {
		return null;
	}

	const { buyerDetails } = orderDetails;

	return (
		<Stack itemsSpacing='12'>
			<InfoLineItem title='Name' value={buyerDetails.name} isCopyable />
			<InfoLineItem title='Buyer ID' value={buyerDetails.buyerId} isCopyable />
			<InfoLineItem title='Email' value={buyerDetails.email} isCopyable />
			<InfoLineItem title='Phone' value={buyerDetails.phone} isCopyable />
			{showAllFields && (
				<>
					<InfoLineItem title='Last active' value={buyerDetails.lastActive} />
					<InfoLineItem
						title='Mailing address'
						value={buyerDetails.mailingAddress}
						isCopyable
					/>
					<InfoLineItem title='Source channel' value={buyerDetails.sourceChannel} />
				</>
			)}
		</Stack>
	);
};

export const BuyerDetailsSection = () => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const { data: orderDetails, isLoading } = useGetOrderDetails(
		selectedRow?.purchasedVehicleId || undefined,
	);

	if (isLoading) {
		return <SectionSkeleton title={SECTION_TITLE} />;
	}

	if (!orderDetails || !selectedRow?.purchasedVehicleId) {
		return null;
	}

	const { buyerDetails } = orderDetails;

	const handleViewLead = () => {
		if (buyerDetails.viewLeadUrl) {
			window.open(buyerDetails.viewLeadUrl, '_blank', 'noopener,noreferrer');
		}
	};

	return (
		<AccordionSection
			title={SECTION_TITLE}
			defaultExpanded
			rightAccessory={
				buyerDetails.viewLeadUrl ? (
					<Button variant='secondary' size='small' onPress={handleViewLead}>
						View lead
					</Button>
				) : undefined
			}>
			<BuyerDetailsContent
				purchasedVehicleId={selectedRow.purchasedVehicleId}
				showAllFields
			/>
		</AccordionSection>
	);
};
